/**
 * Copyright (c) Simam Digital. Built on Project Flowerbed
 * (Copyright (c) Meta Platforms, Inc. and affiliates).
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
	MultiplayerStateComponent,
	SharedWorldComponent,
} from '../../components/MultiplayerComponents';
import {
	claimGift,
	loadPendingGifts,
	sendCoinGift,
} from '../../lib/firebase/GiftStorage';

import { GardenHudSystem } from '../ui/GardenHudSystem';
import { PlayerWalletComponent } from '../../components/EconomyComponents';
import { System } from 'ecsy';
import { getOnlyEntity } from '../../utils/entityUtils';
import { loadAllPlots } from '../../lib/firebase/PlotStorage';
import { notifyGarden } from '../../utils/gardenNotify';

// Fixed cozy gift size: a gift costs the sender this many coins and grants the
// recipient the same. Small + capped so gifting stays a kind gesture, never a
// coin-transfer exploit (the amount is also enforced by firestore.rules).
const GIFT_AMOUNT = 10;

/**
 * Cozy gifting (no adversarial mechanics): a player can send a small coin gift
 * to a neighbour, who claims it on their next visit. Durable delivery is via
 * Firestore (see GiftStorage); when both players are in-world, a Colyseus relay
 * nudges the recipient to claim right away.
 *
 * The send UI is the HUD's neighbours panel (GardenHudOverlay); this system
 * wires it, loads the neighbour list, applies claimed gifts to the wallet, and
 * relays live. Entirely dormant unless the shared world is live (Firebase
 * configured + signed in).
 */
export class GiftingSystem extends System {
	init() {
		this._wired = false;
		this._claimedOnLoad = false;
		this._roomBound = false;
		this._claiming = false;
	}

	execute() {
		const swEntity = getOnlyEntity(this.queries.sharedWorld, false);
		const sharedWorld = swEntity
			? swEntity.getComponent(SharedWorldComponent)
			: null;
		const live = !!(sharedWorld && sharedWorld.configured && sharedWorld.ready);

		this._wireOverlay(live);
		if (!live) return;

		this._bindRoom();

		// Claim any gifts waiting from while the player was away, once per session.
		if (!this._claimedOnLoad) {
			this._claimedOnLoad = true;
			this._claimPending(sharedWorld.uid);
		}
	}

	_overlay() {
		const hudSystem = this.world.getSystem(GardenHudSystem);
		return hudSystem ? hudSystem._overlay : null;
	}

	_wireOverlay(live) {
		if (this._wired) return;
		const overlay = this._overlay();
		if (!overlay || !overlay.setNeighbours) return;
		this._wired = true;

		// Opening the neighbours panel (re)loads the neighbour list. In
		// single-player there are none, so the panel shows its empty state.
		overlay.onOpenGifts = () => {
			if (live) this._loadNeighbours();
			else overlay.setNeighbours([]);
		};

		overlay.onSendGift = (toUid, toName) => this._sendGift(toUid, toName);
	}

	_bindRoom() {
		if (this._roomBound) return;
		const room = this._getRoom();
		if (!room) return;
		this._roomBound = true;

		// A neighbour sent a gift while we're in-world: if it's for us, claim it now.
		room.onMessage('gift_received', (data) => {
			const sharedWorld = this._sharedWorld();
			if (!data || !sharedWorld || data.toUid !== sharedWorld.uid) return;
			this._claimPending(sharedWorld.uid);
		});
	}

	async _loadNeighbours() {
		const overlay = this._overlay();
		if (!overlay) return;
		const sharedWorld = this._sharedWorld();
		if (!sharedWorld) return;
		try {
			const plots = await loadAllPlots();
			const neighbours = plots
				.filter((plot) => plot.ownerUid && plot.ownerUid !== sharedWorld.uid)
				.map((plot) => ({
					uid: plot.ownerUid,
					name: plot.displayName || 'Gardener',
					plantCount: (plot.plants || []).length,
				}));
			overlay.setNeighbours(neighbours);
		} catch (e) {
			console.error('[Gifting] Failed to load neighbours', e);
			overlay.setNeighbours([]);
		}
	}

	async _sendGift(toUid, toName) {
		const sharedWorld = this._sharedWorld();
		if (!sharedWorld || !toUid || toUid === sharedWorld.uid) return;

		const walletEntity = getOnlyEntity(this.queries.wallet, false);
		const wallet = walletEntity
			? walletEntity.getMutableComponent(PlayerWalletComponent)
			: null;
		if (!wallet) return;
		if (wallet.coins < GIFT_AMOUNT) {
			notifyGarden(`Need ${GIFT_AMOUNT} 🪙 to send a gift`, 'error');
			return;
		}

		// Deduct up front so the sender's balance reflects the gift immediately.
		wallet.coins -= GIFT_AMOUNT;
		wallet.dirty = true;

		const fromName = this._localName();
		try {
			await sendCoinGift({
				fromUid: sharedWorld.uid,
				fromName,
				toUid,
				amount: GIFT_AMOUNT,
			});
			// Nudge the recipient if they're in-world right now.
			const room = this._getRoom();
			if (room) {
				room.send('send_gift', { toUid, fromName, amount: GIFT_AMOUNT });
			}
			notifyGarden(
				`🎁 Sent ${GIFT_AMOUNT} 🪙 to ${toName || 'a neighbour'}`,
				'success',
			);
		} catch (e) {
			console.error('[Gifting] Failed to send gift', e);
			// Refund on failure so coins are never silently lost.
			wallet.coins += GIFT_AMOUNT;
			wallet.dirty = true;
			notifyGarden('Could not send gift — try again', 'error');
		}
	}

	async _claimPending(uid) {
		if (this._claiming || !uid) return;
		this._claiming = true;
		try {
			const gifts = await loadPendingGifts(uid);
			if (!gifts.length) return;

			let total = 0;
			const senders = new Set();
			for (const gift of gifts) {
				const amount = Number(gift.amount) || 0;
				if (amount <= 0) continue;
				await claimGift(gift.id);
				total += amount;
				if (gift.fromName) senders.add(gift.fromName);
			}
			if (total <= 0) return;

			const walletEntity = getOnlyEntity(this.queries.wallet, false);
			const wallet = walletEntity
				? walletEntity.getMutableComponent(PlayerWalletComponent)
				: null;
			if (wallet) {
				wallet.coins += total;
				wallet.totalEarned += total;
				wallet.dirty = true;
			}

			const who =
				senders.size === 1 ? [...senders][0] : `${senders.size} neighbours`;
			notifyGarden(`🎁 ${who} gifted you ${total} 🪙!`, 'success');
		} catch (e) {
			console.error('[Gifting] Failed to claim gifts', e);
		} finally {
			this._claiming = false;
		}
	}

	_sharedWorld() {
		const entity = getOnlyEntity(this.queries.sharedWorld, false);
		return entity ? entity.getComponent(SharedWorldComponent) : null;
	}

	_localName() {
		const entity = getOnlyEntity(this.queries.multiplayer, false);
		const state = entity
			? entity.getComponent(MultiplayerStateComponent)
			: null;
		return (state && state.localPlayerName) || 'A gardener';
	}

	_getRoom() {
		const entity = getOnlyEntity(this.queries.multiplayer, false);
		const state = entity
			? entity.getComponent(MultiplayerStateComponent)
			: null;
		return state && state.isConnected ? state.room : null;
	}
}

GiftingSystem.queries = {
	sharedWorld: { components: [SharedWorldComponent] },
	multiplayer: { components: [MultiplayerStateComponent] },
	wallet: { components: [PlayerWalletComponent] },
};
